import React, { useEffect } from "react";
import Select from "react-select";
import { chargeRateOptions } from "@/constants/selectOptions";
import { onlyNumericInput, calculateRentalDuration, comma, uncomma } from "@/utils";
import { useGetInsuranceCorpQuery } from "@/redux/insuranceCorpApi";
import { useGetRentPriceTableQuery, useLazyGetRentPriceTableQuery } from "@/redux/metaApi";

export const InsuranceInfo = ({ formData, handleChange, setFormData, hideFields = [] }) => {
    const { contracts, contractInsurance, contractMechanic } = formData;

    // API 호출: 보험사 업체 조회
    const { data: insuranceCorpData } = useGetInsuranceCorpQuery();
    const insuranceCorpOptions =
        insuranceCorpData && insuranceCorpData.data
            ? insuranceCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
                  receiptNumberPrefix: item.receiptNumberPrefix,
              }))
            : [];

    useEffect(() => {
        let totalDays = 0;
        let totalHours = 0;
        let totalRentFee = 0;
        let totalChargeFee = 0;

        if (Array.isArray(contracts) && contracts.length > 0) {
            contracts.forEach((contract) => {
                const { allocation, billingInfo } = contract;
                // 대여기간 계산: allocation.releasedAt와 allocation.returnedAt이 있을 때
                if (allocation && allocation?.releasedAt && allocation?.returnedAt) {
                    const { days, hours } = calculateRentalDuration(new Date(allocation?.releasedAt), new Date(allocation?.returnedAt));
                    totalDays += days;
                    totalHours += hours;
                }
                // 렌트 요금: billingInfo.rentFee
                if (billingInfo && billingInfo.rentFee) {
                    totalRentFee += Number(uncomma(billingInfo.rentFee)) || 0;
                }
                // 청구 요금: billingInfo.chargeFee
                if (billingInfo && billingInfo.chargeFee) {
                    totalChargeFee += Number(uncomma(billingInfo.chargeFee)) || 0;
                }
            });

            // 만약 총 시간(totalHours)이 24시간 이상이면 추가 일수로 변환
            if (totalHours >= 24) {
                const extraDays = Math.floor(totalHours / 24);
                totalDays += extraDays;
                totalHours = totalHours % 24;
            }

            // formData.contractInsurance 결과 세팅 (contractInsurance가 기존에 없으면 새 객체 생성)
            setFormData((prev) => ({
                ...prev,
                contractInsurance: {
                    ...prev.contractInsurance,
                    totalRentalPeriod: `${totalDays}일 ${totalHours}시간`,
                    days: totalDays,
                    hours: totalHours,
                    totalRentFee,
                    totalChargeFee,
                },
            }));
        }
    }, [contracts, setFormData]);

    // 요금표 목록 조회
    const { data: rentPriceTableData } = useGetRentPriceTableQuery();
    const rentPriceTableOptions = [
        { value: "INSURANCE_BASE", label: "보험사기준" },
        ...(rentPriceTableData && rentPriceTableData.data
            ? rentPriceTableData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : []),
        { value: "INPERSON", label: "직접입력" },
    ];

    // lazy hook 선언: INSURANCE_BASE 선택 시 API 호출에 사용
    const [triggerGetRentPriceTable] = useLazyGetRentPriceTableQuery();

    return (
        <>
            {/* 보험사 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        보험사 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={insuranceCorpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={true}
                            placeholder="보험사 선택"
                            value={insuranceCorpOptions.find((opt) => opt.label === contractInsurance?.insuranceCorpName)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    contractInsurance: {
                                        ...prev.contractInsurance,
                                        insuranceCorpId: option.value,
                                        insuranceCorpName: option.label,
                                        // receiptNumber: option.receiptNumberPrefix,
                                        prefixNumber: option.receiptNumberPrefix,
                                    },
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        접수번호 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    {/* <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.receiptNumber"
                            value={contractInsurance?.receiptNumber || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div> */}
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.prefixNumber"
                            value={contractInsurance?.prefixNumber || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.receiptAddNumber"
                            value={contractInsurance?.receiptAddNumber || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3" style={{ display: "none" }}>
                <div className="row-head">
                    <span>팩스번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.faxNumber"
                            value={contractInsurance?.faxNumber || ""}
                            placeholder=""
                            onChange={handleChange}
                            onInput={onlyNumericInput}
                            maxLength={11}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3"></div>

            <div className="item-row" data-grow="3" style={{ display: "none" }}>
                <div className="row-head">
                    <span>이메일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.email"
                            value={contractInsurance?.email || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3" style={{ display: hideFields.includes("chargeMethod") ? "none" : "inline-flex" }}>
                <div className="row-head">
                    <span>청구 방법 {formData.orderId ? <em>*</em> : <></>}</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        {formData.orderId ? (
                            <Select
                                options={rentPriceTableOptions}
                                className="custom-select"
                                classNamePrefix="custom-select"
                                isSearchable
                                placeholder="청구 방법"
                                value={rentPriceTableOptions?.find((opt) => opt.value === formData.contractInsurance?.chargeMethod)}
                                onChange={(option) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        contractInsurance: {
                                            ...prev.contractInsurance,
                                            chargeMethod: option.value,
                                        },
                                    }));
                                }}
                                isDisabled={formData.orderId}
                            />
                        ) : (
                            <Select
                                options={rentPriceTableOptions}
                                className="custom-select"
                                classNamePrefix="custom-select"
                                isSearchable
                                placeholder={
                                    !formData.contractInsurance?.insuranceCorpId ? "보험사를 먼저 선택해주세요." : "청구 방식 선택"
                                }
                                isDisabled={!formData.contractInsurance?.insuranceCorpId}
                                value={
                                    contractInsurance?.chargeMethod === "INSURANCE_BASE"
                                        ? rentPriceTableOptions.find((opt) => opt.value === "INSURANCE_BASE")
                                        : rentPriceTableOptions.find((opt) => opt.value === contractInsurance?.rentPriceTableId)
                                }
                                onChange={(option) => {
                                    if (option.value === "INSURANCE_BASE") {
                                        if (!formData.contractInsurance?.insuranceCorpId) {
                                            setFormData((prev) => ({
                                                ...prev,
                                                toastMsg: "보험사를 먼저 선택해주세요.",
                                            }));
                                            return;
                                        } else {
                                            // INSURANCE_BASE 선택 시, getRentPriceTable API 호출
                                            triggerGetRentPriceTable({
                                                rent_price_type: "INSURANCE_BASE",
                                            })
                                                .unwrap()
                                                .then((response) => {
                                                    console.log(response);
                                                    // response.data 배열에서 matching insuranceCorp.id 찾기
                                                    const matched = response.data.find(
                                                        (item) =>
                                                            item.insuranceCorp &&
                                                            item.insuranceCorp.id === formData.contractInsurance?.insuranceCorpId
                                                    );
                                                    if (matched) {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            contractInsurance: {
                                                                ...prev.contractInsurance,
                                                                chargeMethod: "INSURANCE_BASE",
                                                                rentPriceTableId: matched.id,
                                                            },
                                                        }));
                                                    } else {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            toastMsg: "해당 보험사에 맞는 요금표가 없습니다.",
                                                        }));
                                                    }
                                                })
                                                .catch((error) => {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        toastMsg: error?.message || "요금표 조회에 실패하였습니다.",
                                                    }));
                                                });
                                        }
                                    } else {
                                        setFormData((prev) => ({
                                            ...prev,
                                            contractInsurance: {
                                                ...prev.contractInsurance,
                                                chargeMethod: "RENT_PRICE",
                                                rentPriceTableId: option.value,
                                            },
                                        }));
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3" style={{ display: "none" }}>
                <div className="row-head">
                    <span>청구율 {formData.orderId ? <em>*</em> : <></>}</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={chargeRateOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable
                            placeholder="청구율"
                            value={chargeRateOptions?.find((opt) => opt.value === contractInsurance?.chargeRate)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    contractInsurance: {
                                        ...prev.contractInsurance,
                                        chargeRate: option.value,
                                    },
                                }));
                            }}
                            isDisabled={formData.orderId}
                        />
                    </div>
                </div>
            </div>

            {!hideFields.includes("chargeMethod") && (
                <>
                    <div className="item-row" data-grow="3"></div>
                    <div className="item-row" data-grow="3"></div>
                </>
            )}

            <div className="item-row" data-grow="3" style={{ display: hideFields.includes("managerName") ? "none" : "inline-flex" }}>
                <div className="row-head">
                    <span>담당자</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.managerName"
                            value={contractInsurance?.managerName || ""}
                            placeholder=""
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3" style={{ display: hideFields.includes("managerPhone") ? "none" : "inline-flex" }}>
                <div className="row-head">
                    <span>담당자 연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="contractInsurance.managerPhone"
                            value={contractInsurance?.managerPhone || ""}
                            placeholder=""
                            onChange={handleChange}
                            onInput={onlyNumericInput}
                            maxLength={11}
                        />
                    </div>
                </div>
            </div>

            {!hideFields.includes("chargeMethod") && (
                <>
                    <div className="item-row" data-grow="3"></div>
                </>
            )}
        </>
    );
};
